package fyi.moko.hotpl.ui.placeform

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fyi.moko.hotpl.data.image.PlaceImageStore
import fyi.moko.hotpl.data.location.LocationContextResolver
import fyi.moko.hotpl.domain.model.EvidenceReason
import fyi.moko.hotpl.domain.model.GeoPoint
import fyi.moko.hotpl.domain.model.PhotoEvidence
import fyi.moko.hotpl.domain.model.PhotoKind
import fyi.moko.hotpl.domain.model.PhotoRef
import fyi.moko.hotpl.domain.model.Place
import fyi.moko.hotpl.domain.model.PlaceStatus
import fyi.moko.hotpl.domain.model.inferVisit
import fyi.moko.hotpl.domain.model.normalizeForComparison
import fyi.moko.hotpl.domain.model.normalizeTags
import fyi.moko.hotpl.domain.photo.PhotoClassifier
import fyi.moko.hotpl.domain.usecase.GetPlaceUseCase
import fyi.moko.hotpl.domain.usecase.ObservePlacesUseCase
import fyi.moko.hotpl.domain.usecase.SavePlaceRejection
import fyi.moko.hotpl.domain.usecase.SavePlaceResult
import fyi.moko.hotpl.domain.usecase.SavePlaceUseCase
import kotlin.time.Clock
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch

/**
 * A newly-picked place photo, not yet written to [PlaceImageStore]. [kind] comes from
 * [PhotoClassifier] and only ever affects cover-photo priority — every photo added here
 * is a place photo regardless of what it looks like (see [PhotoKind.COVER_PRIORITY]).
 */
data class PendingPlacePhoto(
    val bytes: ByteArray,
    val kind: PhotoKind? = null,
    /** EXIF facts read at pick time; they decide [PlaceFormViewModel.status], not the stored file. */
    val evidence: PhotoEvidence = PhotoEvidence(),
) {
    override fun equals(other: Any?): Boolean =
        this === other || (
            other is PendingPlacePhoto &&
                bytes.contentEquals(other.bytes) &&
                kind == other.kind &&
                evidence == other.evidence
            )
    override fun hashCode(): Int =
        (31 * bytes.contentHashCode() + (kind?.hashCode() ?: 0)) * 31 + evidence.hashCode()
}

/**
 * The photo an action sheet is open for in the form.
 *
 * 편집 화면의 사진 타일은 탭 한 번에 바로 지워졌고, 같은 모양 타일이 상세 화면에서는
 * 시트를 여는 탓에 리뷰어가 반복해서 사진을 잃었다(R-6). 두 화면의 탭 의미를 "메뉴를
 * 연다"로 맞추기 위해, 지우기는 시트의 두 번째 탭으로 옮긴다.
 */
sealed interface PlaceFormPhotoTarget {
    /** A photo already stored on the place, identified by its file name. */
    data class Existing(val fileName: String) : PlaceFormPhotoTarget
    /** A photo picked in this session and not yet written, identified by its list position. */
    data class Pending(val index: Int) : PlaceFormPhotoTarget
}

/**
 * Backs both "add" (placeId = null) and "edit" (placeId set) flows.
 * A fresh instance is created per navigation destination.
 */
class PlaceFormViewModel(
    private val getPlace: GetPlaceUseCase,
    private val savePlace: SavePlaceUseCase,
    private val locationContextResolver: LocationContextResolver,
    private val imageStore: PlaceImageStore,
    private val photoClassifier: PhotoClassifier,
    observePlaces: ObservePlacesUseCase,
) : ViewModel() {

    var name by mutableStateOf("")
        private set
    var tagsText by mutableStateOf("")
        private set
    /** Distinct tags already used across all places, for the tag picker chips. */
    var allTags by mutableStateOf<List<String>>(emptyList())
        private set
    var note by mutableStateOf("")
        private set
    /**
     * 편집 시 원래 값을 그대로 돌려보내기 위한 통과 필드. 폼에는 더 이상 스위치가 없고(2026-09-17),
     * 새 장소는 **꺼짐**으로 시작한다 — 예전 기본값 true는 모든 새 장소에 "다시 가고 싶다"를 붙였다.
     */
    private var liked = false
    var isEditing by mutableStateOf(false)
        private set
    var locationContext by mutableStateOf<String?>(null)
        private set
    var latitude: Double? by mutableStateOf(null)
        private set
    var longitude: Double? by mutableStateOf(null)
        private set
    var nameError: String? by mutableStateOf(null)
        private set
    var showDuplicateWarning: Boolean by mutableStateOf(false)
        private set
    /** Set when [performSave] fails; a static, non-technical, retryable message. */
    var saveError: String? by mutableStateOf(null)
        private set
    var resolvingLocationContext by mutableStateOf(false)
        private set
    var loading by mutableStateOf(false)
        private set
    var saving by mutableStateOf(false)
        private set
    /** Photos already on disk (loaded from [loadedPlace]), minus any the user removed. */
    var existingPhotos: List<PhotoRef> by mutableStateOf(emptyList())
        private set
    /** Freshly picked, not-yet-persisted photos (written to the store on save). */
    var pendingPhotos: List<PendingPlacePhoto> by mutableStateOf(emptyList())
        private set
    var classifyingPhotos by mutableStateOf(false)
        private set

    /**
     * 가본 곳인지 아닌지. 추론이 **초깃값만** 정하고, 사용자가 칩을 한 번이라도 누르면
     * 그때부터는 사람이 정한 값이 이긴다([statusChosenByUser]).
     */
    var status by mutableStateOf(PlaceStatus.VISITED)
        private set
    var statusReason by mutableStateOf(EvidenceReason.UNKNOWN)
        private set
    private var statusChosenByUser = false
    /**
     * The pin came from the device, not from a map tap or a photo. Such a pin is only a
     * guess ("you're usually there"); picking Want to go contradicts it, so it is dropped.
     */
    private var pinSuggestedFromDevice = false
    /** Want to go was picked while the pin was the device guess, so the form let it go — say so. */
    var pinDroppedForWishlist by mutableStateOf(false)
        private set
    private var deviceLocation: GeoPoint? = null
    private var deviceFixAgeMillis: Long? = null
    /** 사진 시각으로 잡은 첫 방문 시각. 없으면 저장소가 "지금"으로 찍는다. */
    private var inferredVisitedAt: Long? = null
    /** Non-null while the action sheet for one photo tile is open. */
    var photoActionTarget: PlaceFormPhotoTarget? by mutableStateOf(null)
        private set

    private var started = false
    private var loadedPlace: Place? = null
    private var resolveLocationJob: Job? = null
    private var resolveRequestId = 0

    init {
        viewModelScope.launch {
            observePlaces().collect { places ->
                allTags = places.flatMap { it.tags }.distinct().sorted()
            }
        }
    }

    val isBusy: Boolean get() = loading || saving
    val canSave: Boolean get() = !isBusy && normalizeForComparison(name).isNotBlank()
    /** Tags currently applied to this place, parsed from [tagsText]. */
    val selectedTags: List<String> get() = parseTags(tagsText)

    /**
     * Whether the form has user changes worth confirming before discarding (Cancel).
     * For a new place only typed content counts — the pin is auto-suggested from the
     * current location, so it shouldn't by itself trigger a "discard changes?" prompt.
     * For an edit, any field differing from the loaded place counts.
     */
    val isDirty: Boolean get() {
        val imageChanged = pendingPhotos.isNotEmpty() || existingPhotos != (loadedPlace?.photos ?: emptyList<PhotoRef>())
        val base = loadedPlace
        return if (base == null) {
            name.isNotBlank() || note.isNotBlank() || parseTags(tagsText).isNotEmpty() || imageChanged
        } else {
            name != base.name ||
                note != base.note.orEmpty() ||
                parseTags(tagsText) != base.tags ||
                latitude != base.latitude ||
                longitude != base.longitude ||
                imageChanged
        }
    }

    /** Loads the existing place once when editing. No-op for a new place. */
    fun start(placeId: String?) {
        if (started) return
        started = true
        if (!placeId.isNullOrBlank()) {
            isEditing = true
            loading = true
            viewModelScope.launch {
                try {
                    getPlace(placeId)?.let { place ->
                        loadedPlace = place
                        name = place.name
                        tagsText = place.tags.joinToString(", ")
                        note = place.note.orEmpty()
                        liked = place.liked
                        locationContext = place.locationContext
                        latitude = place.latitude
                        longitude = place.longitude
                        existingPhotos = place.photos
                    }
                } finally {
                    loading = false
                }
            }
        }
    }

    /** 화면이 기기 위치를 알려 준다. 추론의 3·4번 계단이 여기에 달려 있다. */
    fun onDeviceLocation(location: GeoPoint?, fixAgeMillis: Long?) {
        deviceLocation = location
        deviceFixAgeMillis = fixAgeMillis
        refreshInferredStatus()
    }

    /**
     * 사용자가 칩을 눌렀다. 이후 추론은 이 값을 덮지 않고, 추론의 근거 한 줄("You're here right
     * now.")도 더는 맞지 않으므로 지운다.
     *
     * Want to go인데 핀이 기기 위치 *제안*이면 그 핀은 뺀다 — 안 가본 곳이 지금 내 자리일 리
     * 없다(2026-09-20 사용자 검토). 지도에서 직접 찍은 핀이나 사진에서 온 핀은 그대로 둔다.
     * 다시 Been here로 돌아오면 제안을 되살린다 — 실수로 눌렀다가 되돌리는 경우.
     */
    fun onStatusChange(value: PlaceStatus) {
        statusChosenByUser = true
        status = value
        statusReason = EvidenceReason.UNKNOWN
        if (value != PlaceStatus.VISITED) inferredVisitedAt = null
        when {
            value == PlaceStatus.WISHLIST && pinSuggestedFromDevice && !isEditing -> {
                clearLocation()
                pinDroppedForWishlist = true
            }
            value == PlaceStatus.VISITED && pinDroppedForWishlist -> {
                pinDroppedForWishlist = false
                deviceLocation?.let { suggestLocationFromDevice(it.latitude, it.longitude) }
            }
        }
    }

    /**
     * 지금까지 모인 신호로 상태를 다시 고른다. 편집 중인 장소는 건드리지 않는다 — 추론은
     * **생성 시점에만** 개입하고, 이미 정해진 것을 뒤집지 않는다.
     */
    private fun refreshInferredStatus() {
        if (isEditing || statusChosenByUser) return
        val evidence = inferVisit(
            place = latitude?.let { lat -> longitude?.let { lng -> GeoPoint(lat, lng) } },
            photos = pendingPhotos.map { it.evidence },
            currentLocation = deviceLocation,
            fixAgeMillis = deviceFixAgeMillis,
            // viewModelOf(::…)는 생성자 인자를 전부 Koin에서 찾는다 — 기본값 있는 시계를
            // 넣으면 인스턴스 생성이 실패한다. 그래서 여기서는 직접 읽는다.
            now = Clock.System.now().toEpochMilliseconds(),
        )
        status = evidence.status
        statusReason = evidence.reason
        inferredVisitedAt = evidence.visitedAt
    }

    fun onNameChange(value: String) {
        name = value
        nameError = null
        showDuplicateWarning = false
    }
    /** Adds [tag] if not already applied, removes it otherwise. Used by the tag picker chips. */
    fun toggleTag(tag: String) {
        val normalized = normalizeForComparison(tag)
        if (normalized.isEmpty()) return
        val current = selectedTags
        tagsText = if (normalized in current) {
            (current - normalized).joinToString(", ")
        } else {
            (current + normalized).joinToString(", ")
        }
    }
    fun onNoteChange(value: String) { note = value }

    /** Photos were picked from the gallery (already downscaled JPEG bytes each). Classifies
     * each one so cover-photo priority can favor a storefront/food shot over a menu. */
    fun onPhotosPicked(images: List<PickedImage>) {
        if (images.isEmpty()) return
        val room = MAX_PLACE_PHOTOS - existingPhotos.size - pendingPhotos.size
        if (room <= 0) return
        val accepted = images.take(room)
        viewModelScope.launch {
            classifyingPhotos = true
            try {
                val classified = accepted.map { picked ->
                    PendingPlacePhoto(
                        bytes = picked.bytes,
                        kind = photoClassifier.classify(picked.bytes).kind,
                        evidence = picked.evidence(),
                    )
                }
                pendingPhotos = pendingPhotos + classified
                refreshInferredStatus()
            } finally {
                classifyingPhotos = false
            }
        }
    }

    /**
     * Opens the action sheet for a photo tile. Tapping a tile never removes anything on
     * its own — see [PlaceFormPhotoTarget].
     */
    fun openPhotoActions(target: PlaceFormPhotoTarget) { photoActionTarget = target }

    fun dismissPhotoActions() { photoActionTarget = null }

    /** Removes the photo the sheet is open for, and closes it. */
    fun removeTargetedPhoto() {
        when (val target = photoActionTarget) {
            is PlaceFormPhotoTarget.Existing -> onExistingPhotoRemove(target.fileName)
            is PlaceFormPhotoTarget.Pending -> onPendingPhotoRemove(target.index)
            null -> return
        }
        photoActionTarget = null
    }

    fun onPendingPhotoRemove(index: Int) {
        pendingPhotos = pendingPhotos.filterIndexed { i, _ -> i != index }
    }

    fun onExistingPhotoRemove(fileName: String) {
        existingPhotos = existingPhotos.filterNot { it.fileName == fileName }
    }
    /** The user placed the pin (map tap). A deliberate pin outlives a status change. */
    fun onLocationChange(lat: Double, lng: Double) {
        pinSuggestedFromDevice = false
        pinDroppedForWishlist = false
        setLocation(lat, lng)
    }

    /** The screen offers the device position as the pin of a brand-new place. */
    fun suggestLocationFromDevice(lat: Double, lng: Double) {
        pinSuggestedFromDevice = true
        setLocation(lat, lng)
    }

    private fun setLocation(lat: Double, lng: Double) {
        latitude = lat
        longitude = lng
        locationContext = null
        showDuplicateWarning = false
        resolveLocationContext(lat, lng)
        refreshInferredStatus()
    }

    fun clearLocation() {
        resolveLocationJob?.cancel()
        resolveRequestId += 1
        pinSuggestedFromDevice = false
        latitude = null
        longitude = null
        locationContext = null
        resolvingLocationContext = false
        showDuplicateWarning = false
    }

    fun save(onSaved: () -> Unit) {
        performSave(onSaved = onSaved, saveAnyway = false)
    }

    fun saveAnyway(onSaved: () -> Unit) {
        showDuplicateWarning = false
        performSave(onSaved = onSaved, saveAnyway = true)
    }

    fun dismissDuplicateWarning() {
        showDuplicateWarning = false
    }

    fun dismissSaveError() {
        saveError = null
    }

    private fun performSave(onSaved: () -> Unit, saveAnyway: Boolean) {
        if (isBusy) return
        nameError = null
        showDuplicateWarning = false
        saveError = null
        saving = true
        viewModelScope.launch {
            try {
                val photos = resolvePhotos()
                val removedPhotos = (loadedPlace?.photos ?: emptyList()) - existingPhotos.toSet()
                val place = (loadedPlace ?: Place(name = "")).copy(
                    name = name,
                    note = note,
                    liked = liked,
                    locationContext = locationContext,
                    tags = parseTags(tagsText),
                    latitude = latitude,
                    longitude = longitude,
                    photos = photos,
                    // 추론이 정한 상태. 저장소는 VISITED일 때만 첫 방문을 만든다.
                    status = loadedPlace?.status ?: status,
                    // 대표 사진은 저장소가 photos에서 다시 정한다(withDerivedCover). 여기서
                    // null로 비우는 건 "이 폼의 목록이 곧 전부"라는 뜻이다 — 안 비우면 사진을
                    // 전부 지웠을 때 저장소의 폴백이 옛 대표를 되살려, 방금 지운 파일명이 남는다.
                    imageFileName = null,
                )
                when (val result = savePlace(place, saveAnyway = saveAnyway, firstVisitAt = inferredVisitedAt)) {
                    is SavePlaceResult.Saved -> {
                        // Only now that the place is persisted do we drop files that no longer
                        // belong to it (a photo the user removed); on a warning/rejection they
                        // stay intact so a retry doesn't lose them.
                        removedPhotos.forEach { imageStore.delete(it.fileName) }
                        onSaved()
                    }
                    SavePlaceResult.DuplicateNameWarning -> showDuplicateWarning = true
                    is SavePlaceResult.Rejected -> when (result.reason) {
                        SavePlaceRejection.EmptyName -> nameError = "Place name is required"
                    }
                }
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                println("PlaceFormViewModel: save failed: ${e.message ?: e::class.simpleName}")
                saveError = "Couldn't save this place. Please try again."
            } finally {
                saving = false
            }
        }
    }

    private fun resolveLocationContext(lat: Double, lng: Double) {
        resolveLocationJob?.cancel()
        val requestId = ++resolveRequestId
        resolvingLocationContext = true
        resolveLocationJob = viewModelScope.launch {
            val resolved = locationContextResolver.resolve(lat, lng)
            if (requestId == resolveRequestId && latitude == lat && longitude == lng) {
                locationContext = resolved
                resolvingLocationContext = false
            }
        }
    }

    /**
     * [existingPhotos] plus [pendingPhotos] written to the store — cached so a "save
     * anyway" retry after a duplicate-name warning doesn't write the same bytes twice.
     */
    private var writtenPendingPhotos: List<PhotoRef>? = null

    private suspend fun resolvePhotos(): List<PhotoRef> {
        val written = writtenPendingPhotos ?: pendingPhotos.map { pending ->
            PhotoRef(fileName = imageStore.save(pending.bytes), kind = pending.kind)
        }.also { writtenPendingPhotos = it }
        return existingPhotos + written
    }

    private fun parseTags(raw: String): List<String> =
        normalizeTags(raw.split(","))

    companion object {
        /** Total photos a place can carry, existing plus newly picked. */
        const val MAX_PLACE_PHOTOS = 12
    }
}
